/**
 * Tipos de infraestructuras críticas según PTEL (Planes Territoriales de Emergencias Locales)
 * 
 * Basado en análisis de 785 municipios andaluces y Decreto 69/2024 Anexo 1
 * Actualizado con Fase A: Hidráulicas y Energía
 * 
 * @module types/infrastructure
 */

// PAR1: import de solo-tipo. Sin ciclo — `catastroR2` → `catastro` → `tipologia`
// (hoja), y ninguno de los tres importa este módulo.
import type { CatastroR2Parcel } from './catastroR2';
import type { ProcedenciaVia } from '../utils/procedenciaVia';

/**
 * Categorías principales de infraestructuras PTEL
 */
export enum InfrastructureType {
  /** Centros de salud, hospitales, consultorios, ambulatorios (1,500+ en Andalucía) */
  HEALTH = 'SANITARIO',
  
  /** Colegios, institutos, escuelas, guarderías (3,800+ en Andalucía) */
  EDUCATION = 'EDUCATIVO',
  
  /** Comisarías, cuarteles Guardia Civil, policía local (200+ en Andalucía) */
  POLICE = 'POLICIAL',
  
  /** Parques de bomberos (86 en Andalucía según IECA) */
  FIRE = 'BOMBEROS',
  
  /** Museos, bibliotecas, centros culturales, teatros (7,000+ vía IAPH) */
  CULTURAL = 'CULTURAL',
  
  /** Iglesias, ermitas, parroquias, conventos (1,500+ lugares culto) */
  RELIGIOUS = 'RELIGIOSO',
  
  /** Polideportivos, pabellones, campos de fútbol */
  SPORTS = 'DEPORTIVO',
  
  /** Ayuntamientos, oficinas municipales, centros administrativos */
  MUNICIPAL = 'MUNICIPAL',
  
  /** Centros sociales, residencias, centros de día */
  SOCIAL = 'SOCIAL',
  
  /** Gasolineras, estaciones de servicio */
  FUEL = 'COMBUSTIBLE',
  
  /** Centros de protección civil, 112 */
  EMERGENCY = 'EMERGENCIAS',
  
  /** Zonas de riesgo / equipamiento maremoto-tsunami (DERA G12_38) - NUEVO F2 oleada-2 cluster UPCLS3 */
  MAREMOTO = 'MAREMOTO',
  
  /** EDAR, depuradoras, embalses, depósitos agua (4,400+ vía REDIAM) - NUEVO Fase A */
  HYDRAULIC = 'HIDRAULICO',
  
  /** Subestaciones, centrales, parques eólicos, plantas solares (500+ vía Agencia Energía) - NUEVO Fase A */
  ENERGY = 'ENERGIA',
  
  /** Antenas de telecomunicaciones, repetidores, torres de comunicación - NUEVO Fase B */
  TELECOM = 'TELECOM',
  
  /** Carreteras, caminos rurales, vías principales */
  VIAL = 'VIAL',
  
  /** Industrias, polígonos industriales, fábricas */
  INDUSTRIAL = 'INDUSTRIAL',
  
  /** Vertederos, plantas RSU, puntos limpios, ecoparques - NUEVO F2.T7 */
  RSU = 'RSU',
  
  /** Puertos deportivos, pesqueros, comerciales - NUEVO F2.T9 */
  PORT = 'PUERTO',
  
  /** Infraestructura no categorizada - usa geocodificación genérica */
  GENERIC = 'GENERICO'
}

/**
 * Niveles de confianza para clasificación tipológica
 */
export enum ClassificationConfidence {
  /** Match exacto con patrón específico (ej: "Centro de Salud") */
  HIGH = 'ALTA',
  
  /** Match parcial o palabras clave secundarias (ej: "consultorio") */
  MEDIUM = 'MEDIA',
  
  /** Match débil o ambiguo */
  LOW = 'BAJA',
  
  /** No se pudo clasificar - categoría GENERIC */
  NONE = 'NULA'
}

/**
 * Resultado de clasificación tipológica
 */
export interface ClassificationResult {
  /** Tipo de infraestructura detectado */
  type: InfrastructureType;
  
  /** Nivel de confianza en la clasificación */
  confidence: ClassificationConfidence;
  
  /** Patrón regex que hizo match (para debugging) */
  matchedPattern?: string;
  
  /** Palabras clave que activaron la clasificación */
  keywords: string[];
  
  /**
   * Sub-discriminador opcional (γ híbrido cluster UPCLS3 eslabón 40).
   * Solo se rellena cuando `type === InfrastructureType.EMERGENCY` y el texto
   * coincide con keywords de sub-dominio DERA G12 oleada-2:
   * - `SOCORRO`: puestos de socorro, DYA, vigilancia de playa, lifeguard (G12_37)
   * - `HUMANITARIA`: Cruz Roja, organizaciones humanitarias, protección civil voluntaria (G12_36)
   * El orchestrator enruta a `geocodePuestoSocorro()` / `geocodeOrganizacionHumanitaria()`
   * según este sub-tipo. Si no coincide ningún sub-dominio, queda `undefined`
   * y se enruta al flujo EMERGENCY genérico.
   */
  subtype?: 'SOCORRO' | 'HUMANITARIA';
}

/**
 * Resultado de geocodificación (especializada o genérica)
 */
export interface GeocodingResult {
  /** Coordenada X (Este) en EPSG:25830 */
  x: number;
  
  /** Coordenada Y (Norte) en EPSG:25830 */
  y: number;
  
  /** Nivel de confianza 0-100 */
  confidence: number;
  
  /** Fuente de datos que proporcionó las coordenadas */
  source: string;
  
  /** Capa/endpoint específico usado */
  sourceLayer?: string;
  
  /** Query original enviada */
  originalQuery?: string;
  
  /** Nombre exacto encontrado en la base de datos */
  matchedName: string;
  
  /** Tipo de match: 'portal', 'street', 'municipality', 'exact', etc */
  matchType?: string;
  
  /** Score de fuzzy matching (0-1) - solo para WFS especializados */
  fuzzyScore?: number;
  
  /** Dirección completa si está disponible */
  address?: string;
  
  /** Municipio geocodificado */
  municipality?: string;
  
  /** Código INE del municipio (para validación) */
  muniCode?: string;
  
  /** Provincia geocodificada */
  province?: string;
  
  /** Metadatos adicionales según la fuente */
  metadata?: Record<string, unknown>;

  /**
   * PAR1 · Parcela catastral íntegra que ganó la búsqueda, cuando la fuente es
   * Catastro. `HX-CAPTURA-MAXIMAL`: `toGeocodingResult` reducía la parcela a 4
   * claves sueltas dentro de `metadata` y descartaba el resto (`CCB1` midió 29
   * claves en el dato vivo; el tipo declara 32).
   *
   * Va en campo propio y tipado —no ampliando `metadata`— porque varios
   * consumidores esparcen `metadata` con *spread* (`MapViewer`,
   * `scoreRecalculation`, `GeocodingOrchestrator` ×2; `PortsGeocoder` se borró en `PODASRC-b`;
   * `EnriquecedorMetadatos` se borró en `ALBCOT`) y crecerlo filtraría campos sin tipo hacia
   * mapa, score, cotejo y registro de rechazos. Las 5 claves históricas de
   * `metadata` se mantienen intactas por compatibilidad.
   *
   * Procedencia de la atadura: la parcela llega por refcat, polígono/parcela,
   * dirección o *fuzzy* por nombre — **nunca** por proximidad cruda (`search()`
   * no invoca `searchByProximity`, pese a lo que dice su *docstring*). Es la
   * distinción que hace fiable heredar sus atributos, frente al enriquecimiento
   * por vecino más próximo que `CCB1` midió equivocado en 6 de 8.
   */
  catastroParcel?: CatastroR2Parcel;

  /** PROP-E: indica que el resultado requiere revisión manual por incompatibilidad tipológica */
  requiresManualReview?: boolean;

  /** PROP-E: razón de la revisión manual (uso DGC incompatible o fallback sin usoCod) */
  reviewReason?: string;
}

/**
 * Configuración para geocodificación especializada
 */
export interface SpecializedGeocoderConfig {
  /** Endpoint del servicio WFS */
  wfsEndpoint: string;
  
  /** Nombre de la capa WFS */
  layerName: string;
  
  /** Umbral mínimo de fuzzy matching (0-1) */
  fuzzyThreshold: number;
  
  /** Timeout para peticiones WFS en ms */
  timeout: number;
  
  /** Sistema de coordenadas de salida */
  outputSRS: string;
}

/**
 * Configuración para el clasificador tipológico
 */
export interface ClassifierConfig {
  /** Usar clasificación estricta (solo HIGH confidence) */
  strictMode: boolean;
  
  /** Ignorar acentos y mayúsculas en matching */
  caseSensitive: boolean;
}

/**
 * Estadísticas de geocodificación
 */
export interface GeocodingStats {
  /** Total de solicitudes */
  totalRequests: number;
  
  /** Solicitudes exitosas */
  successfulRequests: number;
  
  /** Solicitudes fallidas */
  failedRequests: number;
  
  /** Hits de caché */
  cacheHits: number;
  
  /** Tiempo promedio de respuesta en ms */
  avgResponseTime: number;
}

// =============================================================================
// INFRAESTRUCTURA NORMALIZADA (V04 - Visor Cartográfico)
// =============================================================================

/**
 * Infraestructura normalizada del pipeline PTEL
 * 
 * Representa una infraestructura crítica con coordenadas validadas,
 * lista para visualización en el visor cartográfico.
 * 
 * Las coordenadas están en EPSG:25830 (UTM30 ETRS89).
 * 
 * @example
 * ```typescript
 * const hospital: NormalizedInfrastructure = {
 *   id: 'cs-colomera-001',
 *   name: 'Centro de Salud Colomera',
 *   type: InfrastructureType.HEALTH,
 *   x: 452123,
 *   y: 4145678,
 *   score: 95,
 *   address: 'C/ Real, 1',
 *   municipality: 'Colomera',
 *   province: 'Granada',
 *   source: 'SAS-WFS',
 * };
 * ```
 */
export interface NormalizedInfrastructure {
  /** Identificador único de la infraestructura */
  id: string;
  
  /** Nombre de la infraestructura */
  name: string;
  
  /** Tipo según InfrastructureType */
  type: InfrastructureType;
  
  /** Coordenada X (Este) en EPSG:25830 (metros) */
  x: number;
  
  /** Coordenada Y (Norte) en EPSG:25830 (metros) */
  y: number;
  
  /** Score de validación 0-100 */
  score: number;
  
  /** Dirección normalizada (opcional) */
  address?: string;
  
  /** Municipio */
  municipality?: string;
  
  /** Provincia */
  province?: string;
  
  /** Código INE del municipio */
  muniCode?: string;
  
  /** Fuente de geocodificación (CartoCiudad, SAS-WFS, IECA-DERA, etc.) */
  source?: string;

  /**
   * MAPCEN (13-sep-2026) · vía de la coordenada final según `deriveProcedencia`
   * (la misma que P1/P4 y la ficha de P2). El marcador la usa para distinguir
   * el centroide municipal de una ubicación real; sin ella pinta el símbolo
   * normal. La rellena `toNormalizedInfrastructure`.
   */
  procedencia?: ProcedenciaVia;

  /** MAPCEN · aviso del centroide forzado (`avisoCentroide`), solo si `procedencia === 'centroide'`. */
  aviso?: string;
  
  /** Teléfono de contacto (opcional) */
  phone?: string;
  
  /** Coordenadas originales antes de normalización */
  originalCoords?: {
    x?: number;
    y?: number;
    format?: string;
    crs?: string;
  };
  
  /** Metadatos adicionales según la fuente */
  metadata?: Record<string, unknown>;
}
